import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives';

import { Icon } from '@components/shared/Icon';

import { CourseCard } from './children/CourseCard';
import { LessonCard } from './children/LessonCard';
import { Header } from './children/Header';
import { LearningProgressStatus, SIDEBAR_WIDTH } from './constants';
import { SidebarProvider } from './context/LearningSidebarContext';
import { HoverPopup } from './children/HoverPopup';
import { isEntitySelected, mapLearningProgressToCardStatus } from './utils/utils';
import { ProgressForCourses, LearningPathItem, ProgressForLessons, CourseItem } from './types';
import { svgBadge } from './assets/badge';

export interface LearningSidebarProps {
  isVisible?: boolean;
  isLoading: boolean;
  learningPath?: LearningPathItem;
  progressForLessons?: ProgressForLessons;
  progressForCourses?: ProgressForCourses;
  expandedCourseIds?: string[];
  expandedLessonIds?: string[];
  selectedSectionId?: string;
  currentLessonSlug?: string;
  onToggleVisibility?: () => void;
  renderPopup: (courseData: CourseItem) => React.ReactNode;
  onSelect: (courseSlug: string, lessonSlug?: string, sectionSlug?: string) => void;
  navigate?: (path: string) => void;
}

/**
 * Props for the LearningSidebar component
 * @param {boolean} [isVisible] - Optional flag indicating if the sidebar is visible
 * @param {boolean} isLoading - Flag indicating if the sidebar is in loading state
 * @param {LearningPath} [learningPath] - Optional learning path data
 * @param {ProgressForLessons} [progressForLessons] - Optional progress data for lessons
 * @param {ProgressForCourses} [progressForCourses] - Optional progress data for courses
 * @param {string[]} [expandedCourseIds] - Optional array of expanded course IDs
 * @param {string[]} [expandedLessonIds] - Optional array of expanded lesson IDs
 * @param {() => void} [onToggleVisibility] - Optional function to toggle the visibility of the sidebar
 * @param {(courseData: Course) => React.ReactNode} renderPopup - Function to render the popup
 * @param {(courseSlug: string, lessonSlug?: string, sectionSlug?: string) => void} onSelect - Function to handle selection of course/lesson/section
 * @param {(path: string) => void} [navigate] - Optional function to navigate to a given path
 * @param {string} [selectedSectionId] - Optional ID of the selected section
 * @param {string} [currentLessonSlug] - Optional slug of the current lesson
 */

export const LearningSidebar: React.FC<LearningSidebarProps> = ({
  isVisible = true,
  isLoading,
  learningPath,
  progressForLessons,
  progressForCourses,
  expandedCourseIds = [],
  expandedLessonIds = [],
  selectedSectionId,
  currentLessonSlug,
  onSelect,
  navigate = (): void => {},
  renderPopup,
  onToggleVisibility,
}) => {
  const [showCurrentLessonButton, setShowCurrentLessonButton] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedLessonRef = useRef<HTMLDivElement>(null);

  const courses = learningPath?.coursesCollection.items || [];
  const lessons = courses.flatMap((course) => course.lessonsCollection.items);

  const numberOfCompletedLessons = lessons.filter(
    ({ sys: { id } }) =>
      progressForLessons?.lessons.find((lesson) => lesson.lessonId === id)?.status === LearningProgressStatus.COMPLETED,
  ).length;
  const numberOfCompletedCourses = courses.filter(
    ({ sys: { id } }) =>
      progressForCourses?.courses.find((course) => course.courseId === id)?.status === LearningProgressStatus.COMPLETED,
  ).length;

  const selectedCourse = courses.find(({ slug }) => isEntitySelected(slug));

  const isCourse = !learningPath?.title;

  const calculateProgress = (): number =>
    isCourse ? (numberOfCompletedLessons / lessons.length) * 100 : (numberOfCompletedCourses / courses.length) * 100;

  const completedEntities = isCourse ? numberOfCompletedLessons : numberOfCompletedCourses;
  const totalEntities = isCourse ? lessons.length : courses.length;

  // Find the selected lesson in either the main lessons array or inside course cards
  const selectedLessonId = useMemo(
    () =>
      isCourse
        ? lessons.find(({ slug }) => isEntitySelected('', slug))
        : courses.reduce<string | undefined>((found, course) => {
            if (found) {
              return found;
            }

            const selectedLesson = course.lessonsCollection.items.find(({ slug }) => isEntitySelected('', slug));

            return selectedLesson ? selectedLesson.sys.id : undefined;
          }, undefined),
    [isCourse, lessons, courses],
  );

  // Handle scroll event to check if selected lesson is visible
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !selectedLessonRef.current || !selectedLessonId) {
      return;
    }

    const container = scrollContainerRef.current;
    const selectedLessonElement = selectedLessonRef.current;

    const containerRect = container.getBoundingClientRect();
    const lessonRect = selectedLessonElement.getBoundingClientRect();

    // Check if the selected lesson is above the visible area
    const isLessonAboveViewport = lessonRect.top < containerRect.top;

    setShowCurrentLessonButton(isLessonAboveViewport);
  }, [selectedLessonId]);

  const scrollToSelectedLesson = useCallback(() => {
    if (selectedLessonRef.current && scrollContainerRef.current) {
      selectedLessonRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedLessonId]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [selectedLessonId]);

  return (
    <aside data-testid="sidebar" css={[sidebarStyles, !isVisible && sidebarClosedStyles]}>
      {isVisible && (
        <div css={closeButtonContainerStyles} onClick={onToggleVisibility}>
          <button data-testid="sidebar-mobile-close-button" css={closeButtonStyles}>
            <Icon type="cross" color={token('color.icon')} />
            <span css={closeButtonTextStyles}>Close</span>
          </button>
        </div>
      )}
      <button
        data-testid="sidebar-toggle-button"
        css={[sidebarButton, isCourse && sidebarButtonForCourseStyles]}
        onClick={onToggleVisibility}
        aria-label={isVisible ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        <Icon type={isVisible ? 'sidebar-collapse' : 'sidebar-expand'} />
      </button>
      <Header
        isCourse={isCourse}
        learningPathSlug={learningPath?.slug}
        courseSlug={courses[0]?.slug}
        title={isCourse ? selectedCourse?.title || '' : learningPath?.title || ''}
        completedItems={`${completedEntities}/${totalEntities}`}
        progress={calculateProgress()}
        courseImgUrl={courses[0]?.reward?.badgeCdnUrl || courses[0]?.reward?.badge?.[0]?.cdn_url || svgBadge}
        navigate={navigate}
      />
      <SidebarProvider>
        <div data-testid="sidebar-cards-container" css={cardsContainerStyles}>
          {showCurrentLessonButton && selectedLessonId && (
            <button css={currentLessonStyles} onClick={scrollToSelectedLesson}>
              <Icon type="arrow-up" color={token('color.icon')} />
              <span css={buttonTextStyles}>Current lesson</span>
            </button>
          )}
          <div css={cardsScrollableContainerStyles} ref={scrollContainerRef}>
            {isCourse ? (
              <div data-testid="sidebar-lesson-container" css={lessonContainerStyles}>
                {lessons.map((lesson) => (
                  <div key={lesson.sys.id} ref={isEntitySelected('', lesson.slug) ? selectedLessonRef : null}>
                    <LessonCard
                      courseSlug={selectedCourse?.slug}
                      lesson={lesson}
                      progressForLesson={progressForLessons?.lessons.find(
                        (lessonProgress) => lessonProgress.lessonId === lesson.sys.id,
                      )}
                      onSelect={onSelect}
                      expandedLessonIds={expandedLessonIds}
                      isLoading={isLoading}
                      selectedSectionId={selectedSectionId}
                      currentLessonSlug={currentLessonSlug}
                    />
                  </div>
                ))}
              </div>
            ) : (
              courses.map((course) => (
                <CourseCard
                  course={course}
                  key={course.sys.id}
                  progressForLessons={progressForLessons}
                  status={mapLearningProgressToCardStatus(
                    progressForCourses?.courses.find((courseProgress) => courseProgress.courseId === course.sys.id)
                      ?.status,
                  )}
                  onSelect={onSelect}
                  isLoading={isLoading}
                  selectedLessonRef={selectedLessonRef}
                  expandedLessonIds={expandedLessonIds}
                  expandedCourseIds={expandedCourseIds}
                  selectedSectionId={selectedSectionId}
                  currentLessonSlug={currentLessonSlug}
                />
              ))
            )}
          </div>
        </div>
        <HoverPopup courses={courses} renderPopup={renderPopup} />
      </SidebarProvider>
    </aside>
  );
};

const sidebarStyles = css({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  height: '100%',
  width: `${SIDEBAR_WIDTH}px`,
  borderRight: `1px solid ${token('color.border')}`,
  transition: 'transform 0.3s ease',

  [media.below.xs]: {
    width: '100%',
  },
});

const sidebarClosedStyles = css({
  transform: 'translateX(-412px)',

  [media.below.xs]: {
    transform: 'translateX(-100%)',
  },
});

const closeButtonContainerStyles = css({
  padding: `${token('space.150')} ${token('space.200')}`,

  [media.above.xs]: {
    display: 'none',
  },
});

const closeButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#FFFFFF',
  border: 'none',
  cursor: 'pointer',
});

const closeButtonTextStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: token('color.text'),
  marginLeft: token('space.100'),
});

const sidebarButton = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  width: '32px',
  height: '32px',
  top: '84px',
  right: 'calc(-32px - 16px)',
  backgroundColor: '#FFFFFF',
  borderRadius: '3px',
  boxShadow: '0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 1px 0px rgba(9, 30, 66, 0.25)',
  padding: 0,
  border: 'none',
  cursor: 'pointer',
  zIndex: 100,

  '&:hover': {
    backgroundColor: token('elevation.surface.hovered'),
  },

  '&:active': {
    backgroundColor: token('elevation.surface.pressed'),
  },

  svg: {
    width: '24px',
    height: '24px',
  },

  [media.below.xs]: {
    display: 'none',
  },
});

const sidebarButtonForCourseStyles = css({
  top: token('space.200'),
});

const cardsContainerStyles = css({
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  borderTop: `1px solid ${token('color.border')}`,
  minHeight: 0,
});

const currentLessonStyles = css({
  position: 'absolute',
  zIndex: 100,
  top: token('space.100'),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fff',
  border: `1px solid ${token('color.border')}`,
  padding: `${token('space.100')} ${token('space.200')}`,
  cursor: 'pointer',
  width: 'fit-content',
  borderRadius: '100px',
  boxShadow:
    '0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 8px 12px 0px rgba(9, 30, 66, 0.15), 0px 0px 0px 1px rgba(188, 214, 240, 0.00) inset',
});

const buttonTextStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 400,
  marginLeft: token('space.050'),
  color: token('color.text'),
});

const lessonContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100'),
});

const cardsScrollableContainerStyles = css({
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
});
